import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Plus, Check } from "lucide-react";
import { SPEC_ROWS } from "../data/products";

const MAX = 3;

export const Comparison = ({ products, content, compareRequest, onScrollTo }) => {
  const section = content.comparison;
  const [selected, setSelected] = useState(() => products.slice(0, 2).map((product) => product.id));

  useEffect(() => {
    setSelected((current) => {
      const valid = current.filter((id) => products.some((product) => product.id === id));
      return valid.length ? valid : products.slice(0, 2).map((product) => product.id);
    });
  }, [products]);

  useEffect(() => {
    if (!compareRequest?.id) return;
    setSelected((prev) => {
      if (prev.includes(compareRequest.id)) return prev;
      if (prev.length >= MAX) return [...prev.slice(1), compareRequest.id];
      return [...prev, compareRequest.id];
    });
  }, [compareRequest]);

  const toggle = (id) => {
    setSelected((prev) =>
      prev.includes(id)
        ? prev.filter((x) => x !== id)
        : prev.length >= MAX
        ? [...prev.slice(1), id]
        : [...prev, id]
    );
  };

  const chosen = selected.map((id) => products.find((p) => p.id === id)).filter(Boolean);

  const rowDiffers = (key) => {
    const vals = chosen.map((p) => p.specs[key]);
    return new Set(vals).size > 1;
  };

  return (
    <section id="compare" data-testid="comparison-section" className="relative py-24 lg:py-32 bg-surface-deep">
      <div className="max-w-7xl mx-auto px-5 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-2xl mx-auto mb-12"
        >
          <span className="section-kicker justify-center mb-4">{section.kicker}</span>
          <h2 className="font-display font-bold text-4xl sm:text-5xl">{section.title}</h2>
          <p className="mt-5 text-white/60">
            {section.description}
          </p>
        </motion.div>

        <div className="flex flex-wrap justify-center gap-2.5 mb-10">
          {products.map((p) => {
            const isSel = selected.includes(p.id);
            return (
              <button
                key={p.id}
                onClick={() => toggle(p.id)}
                data-testid={`compare-chip-${p.id}`}
                className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm border transition-colors ${
                  isSel
                    ? "bg-brand text-black border-brand font-semibold"
                    : "border-white/20 text-white/80 hover:border-brand/60"
                }`}
              >
                {isSel ? <Check size={14} /> : <Plus size={14} />}
                {p.name}
              </button>
            );
          })}
        </div>

        {chosen.length === 0 ? (
          <p className="text-center text-white/50">{section.empty}</p>
        ) : (
          <>
            <div className="hidden md:block glass rounded-lg overflow-hidden border border-white/10">
              <table className="w-full" data-testid="comparison-table">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left p-5 text-white/50 font-medium text-sm w-44">{section.specification}</th>
                    {chosen.map((p) => (
                      <th key={p.id} className="p-5 text-center">
                        <img
                          src={p.images[0]}
                          alt={`evLUXE ${p.name} electric scooter comparison image`}
                          className="h-20 mx-auto object-contain mb-2 drop-shadow-[0_14px_28px_rgba(0,0,0,0.45)]"
                          loading="lazy"
                        />
                        <div className="font-display font-semibold">{p.name}</div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {SPEC_ROWS.map((row) => {
                    const diff = rowDiffers(row.key);
                    return (
                      <tr key={row.key} className="border-b border-white/5 last:border-0">
                        <td className="p-4 text-white/55 text-sm">{content.productLabels[row.key] || row.label}</td>
                        {chosen.map((p) => (
                          <td
                            key={p.id}
                            className={`p-4 text-center text-sm font-mono ${
                              diff ? "text-brand" : "text-white/80"
                            }`}
                          >
                            {p.specs[row.key]}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="md:hidden flex gap-4 overflow-x-auto snap-x snap-mandatory pb-4 -mx-5 px-5">
              {chosen.map((p) => (
                <div
                  key={p.id}
                  className="snap-center shrink-0 w-[80vw] glass rounded-lg p-5 border border-white/10"
                  data-testid={`compare-mobile-${p.id}`}
                >
                  <img
                    src={p.images[0]}
                    alt={`evLUXE ${p.name} electric scooter comparison image`}
                    className="h-28 mx-auto object-contain mb-3 drop-shadow-[0_14px_28px_rgba(0,0,0,0.45)]"
                    loading="lazy"
                  />
                  <div className="font-display font-semibold text-center mb-4">{p.name}</div>
                  <div className="divide-y divide-white/5">
                    {SPEC_ROWS.map((row) => (
                      <div key={row.key} className="flex justify-between gap-4 py-2 text-sm">
                        <span className="text-white/45">{content.productLabels[row.key] || row.label}</span>
                        <span className={`font-mono text-right ${rowDiffers(row.key) ? "text-brand" : "text-white/85"}`}>
                          {p.specs[row.key]}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        <div className="text-center mt-12">
          <p className="text-white/60 mb-4">{section.help}</p>
          <button
            onClick={() => onScrollTo("register")}
            data-testid="compare-register-cta"
            className="btn-glow inline-flex items-center bg-brand text-black font-semibold px-7 py-3 rounded-full"
          >
            {content.actions.registerInterest}
          </button>
        </div>
      </div>
    </section>
  );
};

export default Comparison;
